import { Box, Button, Typography, useTheme } from "@mui/material";
import { CheckRounded } from "@mui/icons-material";
import { motion, useReducedMotion } from "framer-motion";
import { OnboardingAnswers } from "../onboardingStorage";
import { REVEAL_COPY, buildPlanItems, planSubject } from "../onboardingSteps";
import { cascadeChild, cascadeParent, riseIn } from "../onboardingMotion";
import {
  GREEN,
  greenText,
  hairline,
  primaryButtonSx,
  textPrimary,
  textSecondary,
} from "./stepStyles";

/**
 * The plan reveal — the flow's payoff. Sits on the same aurora field as the
 * rest of the flow (an earlier full-green treatment shouted over it); glass
 * plan cards cascade in under the animated report card.
 */

interface RevealStepProps {
  answers: OnboardingAnswers;
  onContinue: () => void;
}

export const RevealStep = ({ answers, onContinue }: RevealStepProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();
  const subject = planSubject(answers);

  const rise = (delay: number) => ({
    variants: riseIn(reduced ? 0 : delay),
    initial: "hidden" as const,
    animate: "visible" as const,
  });

  return (
    <Box sx={{ pt: 1, display: "flex", flexDirection: "column", flex: 1 }}>
      <motion.div {...rise(0)}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "2rem",
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
            color: textPrimary(isDark),
          }}
        >
          {REVEAL_COPY.title}
        </Typography>
      </motion.div>
      {subject && (
        <motion.div {...rise(0.06)}>
          {/* Green on purpose: the personalized line is proof we listened. */}
          <Typography
            sx={{
              mt: 1,
              fontSize: "1rem",
              fontWeight: 600,
              lineHeight: 1.5,
              color: greenText(isDark),
            }}
          >
            {subject}
          </Typography>
        </motion.div>
      )}

      {/* Cards (and the closing line) center in the space between the
          headline block and the CTA. */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", py: 2 }}>
      <Box
        component={motion.div}
        variants={reduced ? undefined : cascadeParent}
        initial={reduced ? undefined : "hidden"}
        animate={reduced ? undefined : "visible"}
        sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
      >
        {buildPlanItems(answers).map((item) => (
          <Box
            key={item.title}
            component={motion.div}
            variants={reduced ? undefined : cascadeChild}
            sx={{
              display: "flex",
              gap: 1.5,
              p: 2,
              borderRadius: 3,
              border: `1px solid ${hairline(isDark)}`,
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(12px)",
            }}
          >
            <CheckRounded sx={{ fontSize: 20, color: GREEN, mt: "2px", flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", color: textPrimary(isDark) }}>
                {item.title}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: "0.8125rem", color: textSecondary(isDark) }}>
                {item.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <motion.div {...rise(0.4)}>
        <Typography
          sx={{
            mt: 2,
            fontSize: "0.8125rem",
            fontWeight: 600,
            textAlign: "center",
            color: textSecondary(isDark),
          }}
        >
          {REVEAL_COPY.body}
        </Typography>
      </motion.div>
      </Box>

      <motion.div {...rise(0.5)}>
        <Button fullWidth onClick={onContinue} sx={{ ...primaryButtonSx, mt: 3 }}>
          {REVEAL_COPY.cta}
        </Button>
      </motion.div>
    </Box>
  );
};
